import { useEffect, useMemo, useRef } from 'react';
import { useParentSize } from '../../../hooks/useParentSize';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { SearchField } from '../../SearchField';
import { TableRegions } from '../../Table';
import { TreeTable } from '../../TreeTable';
import './DualSelectorLayout.css';
import cn from './DualSelectorLayout.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const PageableTableLayout = props => {
  const initialDimension = {
    initialDimensions: {
      height: 500,
      width: 500
    }
  };
  const [setRef, dimensions] = useParentSize(initialDimension);
  const localize = useNCTextLocalizer('DualSelector');
  const dt = useDesignTokens();
  const numberOfResults = props.numberOfResults;
  const searchFieldRef = useRef(null);
  const itemString = JSON.stringify([...(props.selections ?? [])].sort());
  const getParentsRowKey = (key, forData) => {
    const keys = [];
    const index = parseInt(key);
    if (index !== -1 && forData[index] !== undefined) {
      const data = forData[index];
      let parentLevel = data.data.level - 1;
      for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
        const {
          level
        } = forData[rowIndex].data;
        if (level === parentLevel) {
          keys.push(forData[rowIndex].key);
          --parentLevel;
        }
      }
    }
    return keys;
  };
  const selectionsInDisplayData = useMemo(() => {
    if (props.selections && props.type === 'tree') {
      return props.selections ? getSelectionsInDisplayData(props.selections, props.data) : undefined;
    }
    return props.selections ? props.selections : undefined;
  }, [props.selections, props.data, props.type]);
  const tableState = props.tableState;
  const prevDisplayedData = usePreviousValue(props.data);
  useEffect(() => {
    const setSelectRows = tableState.setSelectRows;
    const scrollTo = tableState.scrollTo;
    const setExpandedNodes = tableState.setExpandedNodes;
    const selectedNodes = selectionsInDisplayData?.filter(rowIndex => props.data[parseInt(rowIndex)]?.data?.hasChildren !== true);

    // in Tree mode, if the child node is selected then expand it's parent if required
    if (props.type === 'tree' && (props.tableName === 'source' || props.parentNodeSelectable)) {
      const expNodes = new Set(tableState.expandedNodes);
      selectedNodes?.forEach(selectedKey => {
        const parentKeys = getParentsRowKey(selectedKey, props.data);
        parentKeys.forEach(parentKey => {
          const parentNode = props.data.find(value => value.key === parentKey);
          const parentNodeId = parentNode?.data.id;
          if (!expNodes.has(parentNodeId)) {
            expNodes.add(parentNodeId);
          }
        });
      });
      if (expNodes.size !== tableState.expandedNodes.size) {
        setExpandedNodes(expNodes);
      }
    }
    setSelectRows({
      selectedRows: new Set(selectionsInDisplayData)
    });
    let scrollIndex = -1;
    if (props.focusAfterAction) {
      if (Array.isArray(selectionsInDisplayData) && selectionsInDisplayData.length > 0) {
        scrollIndex = parseInt(selectionsInDisplayData[0]);
      } else {
        if (Array.isArray(prevDisplayedData) && props.data.length > prevDisplayedData.length) {
          scrollIndex = prevDisplayedData.findIndex((value, index) => value[props.dataFieldKeys[0]] !== props.data[index][props.dataFieldKeys[0]]);
          if (scrollIndex === -1) {
            scrollIndex = prevDisplayedData.length + 1;
          }
        }
      }
      if (scrollIndex !== -1 && scrollIndex !== tableState.preference.scrollPosition?.cell.rowIndex) {
        scrollTo({
          cell: {
            rowIndex: scrollIndex,
            colIndex: 1,
            region: TableRegions.Data
          },
          align: 'smart'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemString, tableState.setSelectRows, props.dataFieldKeys, props.focusAfterAction, prevDisplayedData, tableState.scrollTo, props.data, tableState.preference.scrollPosition, selectionsInDisplayData]);
  const focusedCell = useRef({
    rowIndex: 0,
    region: props.data.length === 0 && props.hideSearch ? TableRegions.ZeroStateOverlay : TableRegions.Data,
    colIndex: 0
  });
  useEffect(() => {
    // Set the focus on the search field (if hideSearch is false)
    if (props.tableName === 'source' && searchFieldRef.current && !props.preventInitialAutoFocus) {
      searchFieldRef.current.focus();
    }
  }, [props.tableName, props.preventInitialAutoFocus]);
  let defaultZeroStateText = props.tableName === 'target' ? localize('emptySelectedMessage.msg') : localize('emptyAvailableMessage.msg');
  defaultZeroStateText = props.searchValue !== '' && props.numberOfResults === 0 ? localize('noMatchesFound.msg') : defaultZeroStateText;
  const zeroStateText = props.zeroStateText ?? defaultZeroStateText;
  const renderCell = ({
    columnIndex,
    rowIndex,
    columnKey,
    disabled
  }) => {
    return props.renderCell ? props.renderCell({
      columnIndex,
      rowIndex,
      columnKey,
      disabled
    }) : null;
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cn.tableContent,
    "data-testid": props.testId,
    style: renderInlineCssVars({
      'searchfield-spacing': dt.tokens['space-base-xs']
    }, 'dualselector-list'),
    children: [!props.hideSearch ? /*#__PURE__*/_jsx("div", {
      className: cn.listSearchField,
      children: /*#__PURE__*/_jsx(SearchField, {
        testId: postfixTestId(props.testId, 'search'),
        className: cn.searchField,
        inputRef: props.tableName === 'source' ? searchFieldRef : undefined,
        value: props.searchValue ?? '',
        onChange: e => {
          props.filterValue(e.value, props.tableName);
        },
        disabled: props.disableFilter,
        ariaLabel: props.searchAriaLabel,
        numberOfResults: numberOfResults,
        filterMode: true
      })
    }) : null, dimensions ? /*#__PURE__*/_jsx("div", {
      className: cn.tableList,
      onKeyUpCapture: e => {
        if (props.data.length === 0) e.stopPropagation();
      },
      children: /*#__PURE__*/_jsx("div", {
        className: cn.tableListInner,
        ref: setRef,
        children: /*#__PURE__*/_createElement(TreeTable, {
          ...props.tableState,
          testId: postfixTestId(props.testId, `${props.tableName}Table`),
          role: props.type === 'tree' ? 'treegrid' : 'grid',
          key: props.tableName,
          ariaLabel: props.tableAriaLabel,
          rowCount: props.rowCount,
          height: dimensions.height,
          width: dimensions.width,
          columns: props.columns,
          getRowKey: props.getRowKey,
          getRowIndex: key => {
            return parseInt(key);
          },
          getDataNode: props.getDataNode ?? (() => undefined),
          getDataNodeContainer: props.getDataNodeContainer,
          onExpand: props.onExpand,
          renderCellData: props.renderCellData ?? renderCell,
          loadData: props.loadData,
          selectionMode: "desktop",
          onSelect: rows => {
            let selectedRows = rows ? [...rows] : [];
            if (props.tableName === 'source' && props.type === 'tree') {
              selectedRows = (selectedRows ?? []).map(row => {
                return props.data[parseInt(row)]?.originRowIndex.toString();
              });
            }
            props.selectionCallback(selectedRows, props.tableName);
          },
          initialFocusVisible: !props.preventInitialAutoFocus && props.hideSearch && props.tableName === 'source' || false,
          initialFocus: !props.preventInitialAutoFocus && props.tableName === 'source' ? selectionsInDisplayData && selectionsInDisplayData.length > 0 && props.focusAfterAction ? {
            rowIndex: selectionsInDisplayData.length === 1 ? parseInt(selectionsInDisplayData[0]) : focusedCell.current.rowIndex,
            region: TableRegions.Data,
            colIndex: 0
          } : focusedCell.current.rowIndex < 0 ? undefined : focusedCell.current : undefined,
          onFocusChange: focus => {
            focusedCell.current = focus;
          },
          cellBordersVisible: false,
          columnHeadersVisible: false,
          onDoubleClick: ({
            rowKey
          }) => {
            let selectedIndex = parseInt(rowKey);
            if (props.tableName === 'source' && props.type === 'tree') {
              selectedIndex = props.data[selectedIndex].originRowIndex;
            }
            props.onDoubleClick(selectedIndex, props.tableName);
          },
          hideManageColumnsButton: true,
          rowHeight: rowIndex => {
            if (props.rowHeight) {
              return props.rowHeight(rowIndex);
            }
            return 28;
          },
          threshold: props.threshold,
          emptyDataProps: {
            text: zeroStateText
          },
          accessibilityInfo: props.accessibilityInfo,
          expandToFillWidth: true,
          removeFillRows: true
        })
      })
    }) : null]
  });
};
function getSelectionsInDisplayData(selectionsInOriginData, displayData) {
  const result = selectionsInOriginData.map(selection => {
    const originRowIndex = parseInt(selection);
    let displayItemIndex = displayData.findIndex(displayItem => {
      return displayItem.originRowIndex === originRowIndex;
    });
    if (displayItemIndex === -1 && displayData.length > 0) {
      displayItemIndex = displayData[displayData.length - 1].originRowIndex;
    }
    return displayItemIndex.toString();
  });
  return result;
}
//# sourceMappingURL=PageableTableLayout.js.map